/**
 * Where a metric leads (#2175 B3): the record list behind a metric's detail
 * route, and the URL of a metric's detail view inside the Analytics page.
 */

import {
  employeesListUrl,
  invoicesListUrl,
  reconciliationDebtorsUrl,
} from "@/components/cockpit/explorer/lib/drillThrough";
import type { MetricDefinition, MetricScopeSupport } from "@/lib/metrics/types";
import type { MetricScopeParams } from "@/lib/metrics/series";

/** The URL param that opens one metric in the Kennzahlen silo. */
export const METRIC_PARAM = "metric";

/** The record list a metric's detail route opens; null when there is none. */
export function metricRecordsUrl(def: MetricDefinition, to: string): string | null {
  switch (def.detailRoute) {
    case "debtors":
      return reconciliationDebtorsUrl();
    case "employees":
      return employeesListUrl();
    case "groups":
      return "/capacity";
    case "invoices":
      return invoicesListUrl({ year: Number(to.slice(0, 4)), month: Number(to.slice(5, 7)) });
    default:
      return null;
  }
}

/** The current Analytics URL with the Kennzahlen silo on one metric. */
export function metricDetailHref(pathname: string, search: string, id: string): string {
  const params = new URLSearchParams(search);
  params.set("silo", "metrics");
  params.set(METRIC_PARAM, id);
  return `${pathname}?${params.toString()}`;
}

/**
 * Metrics a silo section owns, as on the phone, where the card opens its own
 * screen: the sickness rate opens the Absences section of Staff & Workforce.
 */
export const METRIC_SECTION_TARGETS: Readonly<Record<string, { silo: string; anchor: string }>> = {
  employee_absence_rates: { silo: "staff", anchor: "staff-absences" },
};

/** Where a hub card leads: the section that owns the metric, else the metric's detail. */
export function metricCardHref(pathname: string, search: string, id: string): string {
  const target = METRIC_SECTION_TARGETS[id];
  if (!target) return metricDetailHref(pathname, search, id);
  const params = new URLSearchParams(search);
  params.set("silo", target.silo);
  params.delete(METRIC_PARAM);
  return `${pathname}?${params.toString()}#${target.anchor}`;
}

/** The Analytics page (the Kennzahlen silo is its default). */
export const ANALYTICS_PATH = "/dashboard/analytics";

/**
 * Where a metric leads from outside Analytics (the Dashboard, #2175 C2, C6):
 * the card's own target on the Analytics page, carrying the location and
 * group scope.
 */
export function analyticsMetricHref(id: string, scope: { childcare?: string | null; group?: string | null } = {}): string {
  const params = new URLSearchParams();
  if (scope.childcare) params.set("childcare", scope.childcare);
  if (scope.group) params.set("group", scope.group);
  return metricCardHref(ANALYTICS_PATH, params.toString(), id);
}

/** The figure cannot take a part of the active scope: it is org-wide. */
export function isOrgWideUnderScope(scope: MetricScopeSupport, active: MetricScopeParams): boolean {
  return (!!active.childcare_id && !scope.location) || (!!active.group_id && !scope.group);
}
